import { del, get, post, put, patch } from "services/httpService";

const SERVICE_URLS = {
  getPortfolios: () => "broker-accounts/",
  getPortfolioData: (accountId) => `broker-accounts/${accountId}/data/`,
  createPortfolio: () => "user/broker/",
  deletePortfolio: () => "user/broker/",
  updatePortfolio: (id) => `broker-accounts/${id}/edit/`,
  getPortfolio: (id) => `broker-accounts/${id}/edit/`,
  getPortfolioOverview: (id) => `broker-accounts/${id}/overview/`,
  getPortfoliosByStrategyId: (strategyId) => `broker-accounts/${strategyId}/`,
  getPortfolioOrders: (id) => `broker-accounts/${id}/order-history/`,
  getPortfolioPositions: (id) => `broker-accounts/${id}/positions/`,
  placeOrder: () => `trade/place-order/`,
  getSnaptradeIframeUrl: () => `broker-accounts/oauth-url/?broker=alpaca`,
  getAvailableBrokers: () => `supported-brokers/`,
  getBrokerConnectionLink: () => `broker-accounts/connect/`,
  updatePortfolioName: (id) => `broker/${id}/rename/`,
  reconnectPortfolio: (id) => `broker/${id}/redirect/`,
  updatePortfolioPauseStatus: (id) => `broker-accounts/${id}/pause-status/`,
  getPortfolioTradingDetail: () => `trading-connection/create/`,
  getPortfolioTradingConnectionPositions: (id) =>
    `trading-connection/${id}/positions/`,
  getPortfolioTradingConnectionOrders: (id) =>
    `trading-connection/${id}/orders/`,
  getPortfolioTradingConnectionKeyStats: (id) =>
    `trading-connection/${id}/key-stats/`,
  getPortfolioTradingConnectionHpChart: (id) =>
    `trading-connection/${id}/chart/`,
  getAssetTypes: () => `trade/asset-types/`,
  getTradeAccounts: () => `trade/accounts`,
  getTradeQuote: () => `trade/quote`,
  getTradeAssets: () => `trade/assets`,
  getBuyingPower: (brokerId) => `broker-accounts/${brokerId}/buying-power/`,
  updateTradingConnectionAllocations: () => `trading-connection/allocations/`,
  getAllocationSector: () => `active-strategies/allocation/sectors/chart`,
  getAllocationPosition: () => `broker-accounts/positions/chart`,
  getPerformanceData: () => `trade-logs/charts-data`,
  getPerformanceDataV2: () => `dashboard/performance-chart`,
  getAllocationPositionV2: () => `broker-accounts/positions/chart`,
  getAutoInvesting: () => `dashboard/insights/autoinvesting`,
  getTotalAccountValue: () => `dashboard/insights/totalreturn`,
  getDashboardHistoricalChart: () => `dashboard/insights/historicalcharts`,
  getSurmountAccountCashTransfer: () => `internal-account/cash-transfers/`,
  getSurmountAccountDocuments: () => `internal-account/documents/`,
  getSurmountAccountDetails: () => `internal-account/create-cash-transfer/`,
  getSurmountAccountDocumentDetails: (docId) =>
    `internal-account/documents/${docId}/download/`,

  getAIRiskAssessment: () => `trading-connection/ai-risk-assessment/`,
  getStrategyOverview: () =>
    `broker-accounts/strategy_positions_overview/?broker_accounts=all`,
  getKeyEvents: () => `dashboard/key-events`,
  getKeyEventsDetail: () => `dashboard/key-events/details`,
  getActiveStrategies: () => `active-strategies/`,
  getBrokerAccountConnection: () => `broker-accounts/connection-status/`,
  getCombinedStrategyCharts: () => `dashboard/combined-strategy-charts/`,
  getIndividualBrokerHistoricalCharts: () =>
    `dashboard/insights/individual-broker-historicalcharts/v2`,
  getBrokerAccountRecentTrades: () =>
    `broker-accounts/v2/trades/?broker_account_ids=all`,
};

export const getDashboardHistoricalChart = () =>
  get(SERVICE_URLS.getDashboardHistoricalChart());

export const getTotalAccountValue = () =>
  get(SERVICE_URLS.getTotalAccountValue());

export const getAutoInvesting = (params) =>
  get(SERVICE_URLS.getAutoInvesting(), params);

export const getPerformanceDataV2 = (params) =>
  get(SERVICE_URLS.getPerformanceDataV2(), params);

export const getAllocationPositionV2 = (params) =>
  get(SERVICE_URLS.getAllocationPositionV2(), params);

export const getPerformanceData = () => get(SERVICE_URLS.getPerformanceData());

export const getPortfolios = (params) =>
  get(SERVICE_URLS.getPortfolios(), params);

export const getPortfolioData = (accountId) =>
  get(SERVICE_URLS.getPortfolioData(accountId));

export const getPortfolio = (id) => get(SERVICE_URLS.getPortfolio(id));

export const getPortfolioOverview = (id) =>
  get(SERVICE_URLS.getPortfolioOverview(id));

export const createPortfolio = (payload) =>
  put(SERVICE_URLS.createPortfolio(), payload);

export const updatePortfolio = (id, payload) =>
  put(SERVICE_URLS.updatePortfolio(id), payload);

export const deletePortfolio = (id) => {
  const payload = { broker_account: id };
  return del(SERVICE_URLS.deletePortfolio(), payload);
};

export const getPortfoliosByStrategyId = (strategyId) =>
  get(SERVICE_URLS.getPortfoliosByStrategyId(strategyId));

export const getPortfolioOrders = (id, params) =>
  get(SERVICE_URLS.getPortfolioOrders(id), params);

export const getPortfolioPositions = (id) =>
  get(SERVICE_URLS.getPortfolioPositions(id));

export const placeOrder = (data) => {
  let payload = {
    account_id: data.broker,
    side: data.side,
    symbol: data.symbol,
    quantity: data.amount,
  };

  return post(SERVICE_URLS.placeOrder(), payload);
};

export const getSnaptradeIframeUrl = () =>
  get(SERVICE_URLS.getSnaptradeIframeUrl());

export const getAvailableBrokers = () =>
  get(SERVICE_URLS.getAvailableBrokers());

export const getBrokerConnectionLink = (params) =>
  get(SERVICE_URLS.getBrokerConnectionLink(), params);

export const updatePortfolioName = (data) => {
  const payload = {
    nickname: data.nickname,
  };
  return patch(SERVICE_URLS.updatePortfolioName(data.id), payload);
};

export const reconnectPortfolio = (id) =>
  get(SERVICE_URLS.reconnectPortfolio(id));

export const updatePortfolioPauseStatus = (id, isPaused) => {
  const payload = {
    is_paused: isPaused,
  };
  return patch(SERVICE_URLS.updatePortfolioPauseStatus(id), payload);
};

export const getPortfolioTradingDetail = (id) => {
  const params = {
    account_id: id,
  };
  return get(SERVICE_URLS.getPortfolioTradingDetail(), params);
};

export const getPortfolioTradingConnectionPositions = (id, params) =>
  get(SERVICE_URLS.getPortfolioTradingConnectionPositions(id), params);

export const getPortfolioTradingConnectionOrders = (id, params) =>
  get(SERVICE_URLS.getPortfolioTradingConnectionOrders(id), params);

export const getPortfolioTradingConnectionKeyStats = (id) =>
  get(SERVICE_URLS.getPortfolioTradingConnectionKeyStats(id));

export const getPortfolioTradingConnectionHpChart = (id) =>
  get(SERVICE_URLS.getPortfolioTradingConnectionHpChart(id));

export const getAssetTypes = () => get(SERVICE_URLS.getAssetTypes());

export const getTradeAccounts = (asset) => {
  const params = {
    asset_type: asset,
  };
  return get(SERVICE_URLS.getTradeAccounts(), params);
};

export const getTradeQuote = (symbol) => {
  const params = {
    symbol,
  };
  return get(SERVICE_URLS.getTradeQuote(), params);
};

export const getTradeAssets = (params) =>
  get(SERVICE_URLS.getTradeAssets(), params);

export const getBuyingPower = (brokerId) =>
  get(SERVICE_URLS.getBuyingPower(brokerId));

export const updateTradingConnectionAllocations = (payload) =>
  put(SERVICE_URLS.updateTradingConnectionAllocations(), payload);

export const getAllocationSector = () =>
  get(SERVICE_URLS.getAllocationSector());

export const getAllocationPosition = () =>
  get(SERVICE_URLS.getAllocationPosition());

export const getSurmountAccountCashTransfer = (params) =>
  get(SERVICE_URLS.getSurmountAccountCashTransfer(), params);

export const getSurmountAccountDocuments = () =>
  get(SERVICE_URLS.getSurmountAccountDocuments());

export const getSurmountAccountDetails = () =>
  get(SERVICE_URLS.getSurmountAccountDetails());

export const getSurmountAccountDocumentDetails = (docId) =>
  get(SERVICE_URLS.getSurmountAccountDocumentDetails(docId));

// new
export const getAIRiskAssessment = () =>
  get(SERVICE_URLS.getAIRiskAssessment());

export const getStrategyOverview = () =>
  get(SERVICE_URLS.getStrategyOverview());

export const getKeyEvents = () => get(SERVICE_URLS.getKeyEvents());

export const getKeyEventsDetail = () => get(SERVICE_URLS.getKeyEventsDetail());

export const getActiveStrategies = () =>
  get(SERVICE_URLS.getActiveStrategies());

export const getBrokerAccountConnection = () =>
  get(SERVICE_URLS.getBrokerAccountConnection());

export const getCombinedStrategyCharts = () =>
  get(SERVICE_URLS.getCombinedStrategyCharts());

export const getIndividualBrokerHistoricalCharts = (params) =>
  get(SERVICE_URLS.getIndividualBrokerHistoricalCharts(), params);

export const getBrokerAccountRecentTrades = () =>
  get(SERVICE_URLS.getBrokerAccountRecentTrades());
